import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Input,
  Progress,
  RadioGroup,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
  useDisclosure,
  Badge,
  Dialog,
  Portal,
  Separator,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { IsolateCheckboxesFromField } from '@/components/ui/multi-checkbox-field';
import { FaArrowLeft, FaBullseye, FaCalendarAlt, FaPlus, FaSave, FaTrash, FaUndo } from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaObjetivosSteps } from '../tours/bandaObjetivosTour';
import { bandasAPI } from '../services/api.js';
import { labelTipoActuacion } from '../constants/tipoActuacion.js';
import { useTiposFormacionCatalogo } from '../hooks/useTiposFormacionCatalogo.js';

const SOLO_OPTS = [
  { v: '', label: 'Todo' },
  { v: 'ensayos', label: 'Solo ensayos' },
  { v: 'actuaciones', label: 'Solo actuaciones' },
];
const ORDER_OPTS = [
  { v: 'fecha', label: 'Fecha (antiguas → recientes)' },
  { v: 'fecha_desc', label: 'Fecha (recientes → antiguas)' },
  { v: 'tipo', label: 'Tipo' },
  { v: 'alpha', label: 'Alfabético' },
  { v: 'puntos', label: 'Puntos' },
];

function compareEventosObjetivo(a, b, order) {
  const fa = String(a?.fecha || '');
  const fb = String(b?.fecha || '');
  const ta = String(a?.tipo || '');
  const tb = String(b?.tipo || '');
  const na = String(a?.nombre || '');
  const nb = String(b?.nombre || '');
  if (order === 'tipo') {
    if (ta !== tb) return ta.localeCompare(tb);
    return fa.localeCompare(fb) || na.localeCompare(nb, 'es');
  }
  if (order === 'alpha') {
    return na.localeCompare(nb, 'es') || fa.localeCompare(fb);
  }
  if (order === 'puntos') {
    return (Number(b?.puntos) || 0) - (Number(a?.puntos) || 0) || fa.localeCompare(fb) || na.localeCompare(nb, 'es');
  }
  if (order === 'fecha_desc') {
    return fb.localeCompare(fa) || ta.localeCompare(tb) || na.localeCompare(nb, 'es');
  }
  // fecha (default): antiguas → recientes
  return fa.localeCompare(fb) || ta.localeCompare(tb) || na.localeCompare(nb, 'es');
}

/** Muestra fechas de actividad (ensayo/actuación) como dd/mm/yyyy. */
function formatFechaActividadEs(value) {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  return s;
}

export default function Objetivos() {
  useProductTour('banda-objetivos', bandaObjetivosSteps, { autoStart: false });
  
  const { tipos: tiposFormacionCatalogo } = useTiposFormacionCatalogo();
  const createDisc = useDisclosure();
  const premiosDisc = useDisclosure();
  const puntuacionDisc = useDisclosure();
  const deleteDisc = useDisclosure();
  const cancelDeleteRef = React.useRef(null);
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const sub = useColorModeValue('gray.600', 'gray.300');
  const cardHoverBorder = useColorModeValue('purple.200', 'purple.600');

  const [loadingList, setLoadingList] = useState(false);
  const [objetivos, setObjetivos] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [objetivoQuery, setObjetivoQuery] = useState('');

  const [creating, setCreating] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [form, setForm] = useState({
    nombre: '',
    desde: '',
    hasta: '',
    incluirEnsayos: true,
    incluirActuaciones: true,
    puntosEnsayoDefault: 1,
    puntosActuacionDefault: 3,
    penalizacionEnsayoAusenciaDefault: 0,
    penalizacionActuacionAusenciaDefault: 0,
  });

  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [solo, setSolo] = useState('');
  const [order, setOrder] = useState('fecha');
  /** Orden solo para la lista «Por actividad concreta» del modal de puntuación. */
  const [overrideFechaOrder, setOverrideFechaOrder] = useState('fecha_desc');

  const [premiosDraft, setPremiosDraft] = useState([]);
  const [premiosDirty, setPremiosDirty] = useState(false);
  const [savingPremios, setSavingPremios] = useState(false);

  const [overridesDraft, setOverridesDraft] = useState({});

  const [baseDraft, setBaseDraft] = useState(null);
  const [puntuacionDirty, setPuntuacionDirty] = useState(false);
  const [savingPuntuacion, setSavingPuntuacion] = useState(false);
  const [deletingObjetivo, setDeletingObjetivo] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoadingList(true);
      const res = await bandasAPI.listObjetivos();
      const list = Array.isArray(res.data) ? res.data : [];
      setObjetivos(list);
      setSelectedId((prev) => (prev && list.some((o) => o?.id === prev) ? prev : ''));
    } catch {
      toaster.create({ title: 'No se pudieron cargar los objetivos', type: 'error' });
      setObjetivos([]);
      setSelectedId('');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const objetivosFiltered = useMemo(() => {
    const q = String(objetivoQuery || '')
      .trim()
      .toLowerCase();
    const list = Array.isArray(objetivos) ? objetivos : [];
    if (!q) return list;
    return list.filter((o) => {
      const s = `${o?.nombre || ''} ${o?.desde || ''} ${o?.hasta || ''}`.toLowerCase();
      return s.includes(q);
    });
  }, [objetivos, objetivoQuery]);

  const selectedObj = useMemo(() => {
    const list = Array.isArray(objetivos) ? objetivos : [];
    return list.find((o) => o?.id === selectedId) || null;
  }, [objetivos, selectedId]);

  const backToList = useCallback(() => {
    setSelectedId('');
    setDetalle(null);
    setSolo('');
    setOrder('fecha');
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) {
      setDetalle(null);
      return;
    }
    try {
      setDetalleLoading(true);
      const res = await bandasAPI.getObjetivo(selectedId, { solo: solo || undefined, order: order || undefined });
      setDetalle(res.data || null);
    } catch {
      toaster.create({ title: 'No se pudo cargar el objetivo', type: 'error' });
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  }, [selectedId, solo, order]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const handleCreate = async () => {
    if (!form.nombre.trim() || !form.desde || !form.hasta) {
      toaster.create({ title: 'Nombre y fechas son obligatorios', type: 'warning' });
      return;
    }
    try {
      setCreating(true);
      const res = await bandasAPI.createObjetivo(form);
      const id = res.data?.id;
      await loadList();
      if (id) setSelectedId(id);
      toaster.create({ title: 'Objetivo creado', type: 'success' });
      createDisc.onClose();
      setCreateStep(1);
      setForm({
        nombre: '',
        desde: '',
        hasta: '',
        incluirEnsayos: true,
        incluirActuaciones: true,
        puntosEnsayoDefault: 1,
        puntosActuacionDefault: 3,
        penalizacionEnsayoAusenciaDefault: 0,
        penalizacionActuacionAusenciaDefault: 0,
      });
    } catch (e) {
      toaster.create({
        title: 'No se pudo crear',
        description: e?.response?.data?.message || undefined,
        type: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const openCreate = () => {
    setCreateStep(1);
    setForm({
      nombre: '',
      desde: '',
      hasta: '',
      incluirEnsayos: true,
      incluirActuaciones: true,
      puntosEnsayoDefault: 1,
      puntosActuacionDefault: 3,
      penalizacionEnsayoAusenciaDefault: 0,
      penalizacionActuacionAusenciaDefault: 0,
    });
    createDisc.onOpen();
  };

  const objetivo = detalle?.objetivo || null;
  const eventos = Array.isArray(detalle?.eventos) ? detalle.eventos : [];
  const eventosOrdenados = useMemo(
    () => [...eventos].sort((a, b) => compareEventosObjetivo(a, b, order)),
    [eventos, order],
  );
  const eventosOverridesOrdenados = useMemo(
    () => [...eventos].sort((a, b) => compareEventosObjetivo(a, b, overrideFechaOrder)),
    [eventos, overrideFechaOrder],
  );
  const filas = Array.isArray(detalle?.filas) ? detalle.filas : [];

  useEffect(() => {
    const ps = Array.isArray(objetivo?.premios) ? objetivo.premios : [];
    const mapped = ps
      .slice()
      .sort((a, b) => (a.puntosMin ?? 0) - (b.puntosMin ?? 0) || (a.orden ?? 0) - (b.orden ?? 0))
      .map((p) => ({ puntosMin: Number(p?.puntosMin ?? 0) || 0, premio: String(p?.premio || '') }));
    setPremiosDraft(mapped);
    setPremiosDirty(false);
  }, [objetivo?.id]);

  const maxColsHint = eventosOrdenados.length > 18;
  const headerStickyBg = useColorModeValue('white', 'gray.800');
  const justificadoNumBg = useColorModeValue('cyan.100', 'cyan.900');
  const justificadoNumBorder = useColorModeValue('cyan.300', 'cyan.600');
  const justificadoNumColor = useColorModeValue('cyan.900', 'cyan.100');
  const justificadoLegendSwatch = useColorModeValue('cyan.100', 'cyan.800');

  const premiosTxt = useMemo(() => {
    const ps = Array.isArray(objetivo?.premios) ? objetivo.premios : [];
    if (!ps.length) return '';
    return ps
      .slice()
      .sort((a, b) => (a.puntosMin ?? 0) - (b.puntosMin ?? 0))
      .map((p) => `${p.puntosMin} → ${p.premio}`)
      .join(' · ');
  }, [objetivo?.premios]);

  const handlePremioChange = (idx, patch) => {
    setPremiosDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setPremiosDirty(true);
  };

  const handleAddPremio = () => {
    setPremiosDraft((rows) => [...rows, { puntosMin: 0, premio: '' }]);
    setPremiosDirty(true);
  };

  const handleRemovePremio = (idx) => {
    setPremiosDraft((rows) => rows.filter((_, i) => i !== idx));
    setPremiosDirty(true);
  };

  const handleResetPremios = () => {
    const ps = Array.isArray(objetivo?.premios) ? objetivo.premios : [];
    const mapped = ps
      .slice()
      .sort((a, b) => (a.puntosMin ?? 0) - (b.puntosMin ?? 0) || (a.orden ?? 0) - (b.orden ?? 0))
      .map((p) => ({ puntosMin: Number(p?.puntosMin ?? 0) || 0, premio: String(p?.premio || '') }));
    setPremiosDraft(mapped);
    setPremiosDirty(false);
  };

  const handleSavePremios = async () => {
    if (!selectedId) return;
    try {
      setSavingPremios(true);
      const clean = premiosDraft
        .map((p) => ({
          puntosMin: Math.max(0, Math.round(Number(p?.puntosMin) || 0)),
          premio: String(p?.premio || '').trim(),
        }))
        .filter((p) => p.premio || p.puntosMin > 0);
      await bandasAPI.patchObjetivo(selectedId, { premios: clean });
      toaster.create({ title: 'Premios actualizados', type: 'success' });
      setPremiosDirty(false);
      await loadDetail();
      premiosDisc.onClose();
    } catch (e) {
      toaster.create({
        title: 'No se pudieron guardar los premios',
        description: e?.response?.data?.message || undefined,
        type: 'error',
      });
    } finally {
      setSavingPremios(false);
    }
  };

  const openPremios = () => {
    const ps = Array.isArray(objetivo?.premios) ? objetivo.premios : [];
    const mapped = ps
      .slice()
      .sort((a, b) => (a.puntosMin ?? 0) - (b.puntosMin ?? 0) || (a.orden ?? 0) - (b.orden ?? 0))
      .map((p) => ({ puntosMin: Number(p?.puntosMin ?? 0) || 0, premio: String(p?.premio || '') }));
    setPremiosDraft(mapped);
    setPremiosDirty(false);
    premiosDisc.onOpen();
  };

  const buildOverridesDraftFromObjetivo = (o) => {
    const next = {};
    const ovs = Array.isArray(o?.overrides) ? o.overrides : [];
    for (const ov of ovs) {
      const key = ov?.tipo === 'ACTUACION' ? `actuacion:${ov.actuacionId}` : `ensayo:${ov.ensayoId}`;
      if (key.includes('undefined') || key.endsWith(':')) continue;
      next[key] = {
        puntos: Number(ov?.puntos ?? 0) || 0,
        penalizacionAusencia:
          ov?.penalizacionAusencia === null || ov?.penalizacionAusencia === undefined
            ? undefined
            : Number(ov.penalizacionAusencia) || 0,
      };
    }
    return next;
  };

  const buildBaseDraftFromObjetivo = (o) => {
    const map =
      o.actuacionPuntosPorTipo && typeof o.actuacionPuntosPorTipo === 'object' && !Array.isArray(o.actuacionPuntosPorTipo)
        ? o.actuacionPuntosPorTipo
        : {};
    const porTipo = {};
    for (const t of tiposFormacionCatalogo) {
      const key = String(t?.codigo || '').trim();
      if (!key) continue;
      const row = map[key];
      porTipo[key] = {
        puntos: String(row?.puntos ?? o.puntosActuacionDefault ?? 3),
        penalizacionAusencia:
          row?.penalizacionAusencia != null && row?.penalizacionAusencia !== undefined
            ? String(row.penalizacionAusencia)
            : '',
      };
    }
    return {
      puntosEnsayoDefault: o.puntosEnsayoDefault ?? 1,
      penalizacionEnsayoAusenciaDefault: o.penalizacionEnsayoAusenciaDefault ?? 0,
      puntosActuacionDefault: o.puntosActuacionDefault ?? 3,
      penalizacionActuacionAusenciaDefault: o.penalizacionActuacionAusenciaDefault ?? 0,
      actuacionPuntosModo: o.actuacionPuntosModo === 'POR_TIPO' ? 'POR_TIPO' : 'GLOBAL',
      porTipo,
    };
  };

  const openPuntuacion = () => {
    const o = detalle?.objetivo;
    if (!o) return;
    setBaseDraft(buildBaseDraftFromObjetivo(o));
    setOverridesDraft(buildOverridesDraftFromObjetivo(o));
    setPuntuacionDirty(false);
    puntuacionDisc.onOpen();
  };

  const handleResetPuntuacion = () => {
    const o = detalle?.objetivo;
    if (!o) return;
    setBaseDraft(buildBaseDraftFromObjetivo(o));
    setOverridesDraft(buildOverridesDraftFromObjetivo(o));
    setPuntuacionDirty(false);
  };

  const onBaseDraftChange = (updater) => {
    setPuntuacionDirty(true);
    setBaseDraft(updater);
  };

  const handleOverrideChange = (evKey, patch) => {
    setPuntuacionDirty(true);
    setOverridesDraft((m) => ({ ...(m || {}), [evKey]: { ...(m?.[evKey] || {}), ...patch } }));
  };

  const handleOverrideClear = (evKey) => {
    setPuntuacionDirty(true);
    setOverridesDraft((m) => {
      const copy = { ...(m || {}) };
      delete copy[evKey];
      return copy;
    });
  };

  const handleSavePuntuacion = async () => {
    if (!selectedId || !baseDraft) return;
    try {
      setSavingPuntuacion(true);
      const payload = {
        puntosEnsayoDefault: Math.max(0, Math.round(Number(baseDraft.puntosEnsayoDefault) || 0)),
        penalizacionEnsayoAusenciaDefault: Math.max(0, Math.round(Number(baseDraft.penalizacionEnsayoAusenciaDefault) || 0)),
        puntosActuacionDefault: Math.max(0, Math.round(Number(baseDraft.puntosActuacionDefault) || 0)),
        penalizacionActuacionAusenciaDefault: Math.max(0, Math.round(Number(baseDraft.penalizacionActuacionAusenciaDefault) || 0)),
        actuacionPuntosModo: baseDraft.actuacionPuntosModo,
      };
      if (baseDraft.actuacionPuntosModo === 'POR_TIPO') {
        const porTipoBody = {};
        for (const t of tiposFormacionCatalogo) {
          const key = String(t?.codigo || '').trim();
          if (!key) continue;
          const row = baseDraft.porTipo[key] || { puntos: '0', penalizacionAusencia: '' };
          const pts = Math.max(0, Math.round(Number(row.puntos) || 0));
          const penStr = String(row.penalizacionAusencia ?? '').trim();
          const entry = { puntos: pts };
          if (penStr !== '') entry.penalizacionAusencia = Math.max(0, Math.round(Number(penStr) || 0));
          porTipoBody[key] = entry;
        }
        payload.actuacionPuntosPorTipo = porTipoBody;
      } else {
        payload.actuacionPuntosPorTipo = null;
      }

      const entries = Object.entries(overridesDraft || {});
      const overrides = entries
        .filter(([k]) => typeof k === 'string' && (k.startsWith('ensayo:') || k.startsWith('actuacion:')))
        .map(([k, v]) => {
          const [t, id] = k.split(':');
          const puntos = Math.max(0, Math.round(Number(v?.puntos) || 0));
          const penalizacionAusencia =
            v?.penalizacionAusencia === undefined || v?.penalizacionAusencia === null
              ? undefined
              : Math.max(0, Math.round(Number(v.penalizacionAusencia) || 0));
          if (!id) return null;
          if (t === 'ensayo') return { tipo: 'ENSAYO', ensayoId: id, puntos, penalizacionAusencia };
          return { tipo: 'ACTUACION', actuacionId: id, puntos, penalizacionAusencia };
        })
        .filter(Boolean);

      payload.overrides = overrides;

      await bandasAPI.patchObjetivo(selectedId, payload);
      toaster.create({ title: 'Puntuación guardada', type: 'success' });
      puntuacionDisc.onClose();
      setBaseDraft(null);
      setOverridesDraft({});
      setPuntuacionDirty(false);
      await loadDetail();
    } catch (e) {
      toaster.create({
        title: 'No se pudo guardar',
        description: e?.response?.data?.message || undefined,
        type: 'error',
      });
    } finally {
      setSavingPuntuacion(false);
    }
  };

  const handleDeleteObjetivo = async () => {
    if (!selectedId) return;
    try {
      setDeletingObjetivo(true);
      await bandasAPI.deleteObjetivo(selectedId);
      toaster.create({ title: 'Objetivo eliminado', type: 'success' });
      deleteDisc.onClose();
      setDetalle(null);
      setSelectedId('');
      await loadList();
    } catch (e) {
      toaster.create({
        title: 'No se pudo eliminar',
        description: e?.response?.data?.message || undefined,
        type: 'error',
      });
    } finally {
      setDeletingObjetivo(false);
    }
  };

  return (
    <AppLayout userType="banda">
      <Box bg={bg} minH="100vh" py={{ base: 4, md: 8 }} px={4}>
        <Box maxW="7xl" mx="auto">
          <VStack align="stretch" gap={6}>
            {!selectedId ? (
              <>
                <HStack gap={3} align="flex-start" justify="space-between" flexWrap="wrap" data-tour="objetivos-header">
                  <Box color="purple.500" pt={1}>
                    <FaBullseye size={28} />
                  </Box>
                  <Box flex="1" minW={0}>
                    <Heading size="lg">Objetivos</Heading>
                    <Text color={sub} fontSize="sm" mt={1}>
                      Mide el compromiso de tus músicos por asistencia. Elige un periodo para ver ranking, filtros y
                      configuración de puntos.
                    </Text>
                  </Box>
                  <Button colorPalette="purple" onClick={openCreate} data-tour="objetivos-crear">
                    <FaPlus />
                    Crear objetivo
                  </Button>
                </HStack>

                {loadingList ? (
                  <Flex justify="center" py={10}>
                    <Spinner color="purple.500" size="lg" />
                  </Flex>
                ) : objetivos.length === 0 ? (
                  <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderCol} rounded="2xl" shadow="md">
                    <Card.Body py={10} textAlign="center">
                      <Box color="purple.400" mb={3} display="inline-flex">
                        <FaBullseye size={40} />
                      </Box>
                      <Heading size="sm" mb={2}>
                        Aún no has creado ningún objetivo
                      </Heading>
                      <Text fontSize="sm" color={sub} mb={4}>
                        Crea uno para empezar a medir el compromiso de la plantilla.
                      </Text>
                      <Button colorPalette="purple" onClick={openCreate}>
                        <FaPlus />
                        Crear objetivo
                      </Button>
                    </Card.Body>
                  </Card.Root>
                ) : (
                  <VStack align="stretch" gap={4} data-tour="objetivos-listado">
                    {objetivos.length > 3 ? (
                      <Input
                        placeholder="Buscar por nombre o fecha…"
                        value={objetivoQuery}
                        onChange={(e) => setObjetivoQuery(e.target.value)}
                        bg={cardBg}
                        maxW={{ md: '360px' }}
                      />
                    ) : null}
                    {objetivosFiltered.length === 0 ? (
                      <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderCol} rounded="xl">
                        <Card.Body textAlign="center" py={8}>
                          <Text fontSize="sm" color={sub}>
                            No hay resultados para «{objetivoQuery}».
                          </Text>
                        </Card.Body>
                      </Card.Root>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                        {objetivosFiltered.map((o) => (
                          <Card.Root
                            key={o.id}
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderCol}
                            rounded="2xl"
                            shadow="md"
                            cursor="pointer"
                            transition="all 0.2s"
                            _hover={{ shadow: 'xl', transform: 'translateY(-3px)', borderColor: cardHoverBorder }}
                            onClick={() => {
                              setObjetivoQuery('');
                              setSelectedId(o.id);
                            }}
                          >
                            <Box
                              h="72px"
                              bgGradient="linear(135deg, purple.700, purple.500, purple.300)"
                              roundedTop="2xl"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Box color="whiteAlpha.400">
                                <FaBullseye size={28} />
                              </Box>
                            </Box>
                            <Card.Body>
                              <Text fontWeight="900" fontSize="md" lineClamp={2} mb={2} title={o.nombre}>
                                {o.nombre}
                              </Text>
                              <HStack gap={2} color={sub} fontSize="sm" mb={3}>
                                <FaCalendarAlt />
                                <Text>
                                  {o.desde} → {o.hasta}
                                </Text>
                              </HStack>
                              <HStack gap={2} flexWrap="wrap">
                                {o.incluirEnsayos ? (
                                  <Badge colorPalette="blue" variant="subtle">
                                    Ensayos
                                  </Badge>
                                ) : null}
                                {o.incluirActuaciones ? (
                                  <Badge colorPalette="purple" variant="subtle">
                                    Actuaciones
                                  </Badge>
                                ) : null}
                              </HStack>
                            </Card.Body>
                          </Card.Root>
                        ))}
                      </SimpleGrid>
                    )}
                  </VStack>
                )}
              </>
            ) : (
              <HStack gap={3} align="center" flexWrap="wrap" data-tour="objetivos-header">
                <Button variant="ghost" onClick={backToList} aria-label="Volver al listado">
                  <FaArrowLeft />
                  Volver
                </Button>
                <Box flex="1" minW={0}>
                  <Heading size="lg" lineClamp={2}>
                    {selectedObj?.nombre || objetivo?.nombre || 'Objetivo'}
                  </Heading>
                  {(selectedObj || objetivo) && (
                    <Text color={sub} fontSize="sm" mt={1}>
                      {(selectedObj || objetivo).desde} → {(selectedObj || objetivo).hasta}
                    </Text>
                  )}
                </Box>
              </HStack>
            )}

            <Dialog.Root open={createDisc.open} size='lg' placement='center' onOpenChange={e => {
              if (!e.open) {
                createDisc.onClose();
              }
            }}>
              <Portal>

                <Dialog.Backdrop />
                <Dialog.Positioner>
                  <Dialog.Content>
                    <Dialog.Header>Crear objetivo</Dialog.Header>
                    <Dialog.CloseTrigger />
                    <Progress.Root value={(createStep / 3) * 100} size="xs" colorPalette="purple">
                      <Progress.Track>
                        <Progress.Range />
                      </Progress.Track>
                    </Progress.Root>
                    <Dialog.Body pt={5}>
                      {createStep === 1 ? (
                        <VStack align="stretch" gap={3}>
                          <Field.Root required>
                            <Field.Label fontSize="sm">Nombre del periodo</Field.Label>
                            <Input
                              placeholder="Ej: Compromiso mayo-junio 2026"
                              value={form.nombre}
                              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                            />
                          </Field.Root>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                            <Field.Root required>
                              <Field.Label fontSize="sm">Inicio</Field.Label>
                              <Input type="date" value={form.desde} onChange={(e) => setForm((f) => ({ ...f, desde: e.target.value }))} />
                            </Field.Root>
                            <Field.Root required>
                              <Field.Label fontSize="sm">Fin</Field.Label>
                              <Input type="date" value={form.hasta} onChange={(e) => setForm((f) => ({ ...f, hasta: e.target.value }))} />
                            </Field.Root>
                          </SimpleGrid>
                          <Text fontSize="xs" color={sub}>
                            Paso 1/3 · Define el periodo a medir.
                          </Text>
                        </VStack>
                      ) : null}

                      {createStep === 2 ? (
                        <VStack align="stretch" gap={3}>
                          <Box>
                            <Text fontSize="sm" fontWeight="800" mb={2}>
                              ¿Qué actividades cuentan?
                            </Text>
                            <IsolateCheckboxesFromField>
                            <HStack gap={5} flexWrap="wrap">
                              <Checkbox.Root
                                onCheckedChange={(details) => setForm((f) => ({ ...f, incluirEnsayos: details.checked === true }))}
                                checked={form.incluirEnsayos}
                              ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Ensayos
                                                            </Checkbox.Label></Checkbox.Root>
                              <Checkbox.Root
                                onCheckedChange={(details) => setForm((f) => ({ ...f, incluirActuaciones: details.checked === true }))}
                                checked={form.incluirActuaciones}
                              ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Actuaciones
                                                            </Checkbox.Label></Checkbox.Root>
                            </HStack>
                            </IsolateCheckboxesFromField>
                          </Box>
                          <Separator />
                          <Text fontSize="xs" color={sub}>
                            Paso 2/3 · Puedes dejar ambos activados.
                          </Text>
                        </VStack>
                      ) : null}

                      {createStep === 3 ? (
                        <VStack align="stretch" gap={3}>
                          <Text fontSize="sm" fontWeight="800">
                            Puntuación por defecto
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                            <Field.Root disabled={!form.incluirEnsayos}>
                              <Field.Label fontSize="sm">Puntos por ensayo (PRESENTE)</Field.Label>
                              <Input
                                type="number"
                                value={form.puntosEnsayoDefault}
                                onChange={(e) => setForm((f) => ({ ...f, puntosEnsayoDefault: Number(e.target.value) }))}
                                min={0}
                              />
                            </Field.Root>
                            <Field.Root disabled={!form.incluirActuaciones}>
                              <Field.Label fontSize="sm">Puntos por actuación (asiste = sí)</Field.Label>
                              <Input
                                type="number"
                                value={form.puntosActuacionDefault}
                                onChange={(e) => setForm((f) => ({ ...f, puntosActuacionDefault: Number(e.target.value) }))}
                                min={0}
                              />
                            </Field.Root>
                          </SimpleGrid>
                          <Separator />
                          <Text fontSize="sm" fontWeight="800">
                            Penalización por ausencia
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                            <Field.Root disabled={!form.incluirEnsayos}>
                              <Field.Label fontSize="sm">Penaliza ensayo (AUSENTE)</Field.Label>
                              <Input
                                type="number"
                                value={form.penalizacionEnsayoAusenciaDefault}
                                onChange={(e) => setForm((f) => ({ ...f, penalizacionEnsayoAusenciaDefault: Number(e.target.value) }))}
                                min={0}
                              />
                            </Field.Root>
                            <Field.Root disabled={!form.incluirActuaciones}>
                              <Field.Label fontSize="sm">Penaliza actuación (asiste = no)</Field.Label>
                              <Input
                                type="number"
                                value={form.penalizacionActuacionAusenciaDefault}
                                onChange={(e) => setForm((f) => ({ ...f, penalizacionActuacionAusenciaDefault: Number(e.target.value) }))}
                                min={0}
                              />
                            </Field.Root>
                          </SimpleGrid>
                          <Text fontSize="xs" color={sub}>
                            Paso 3/3 · Luego podrás ajustar premios y puntuación en el detalle del objetivo.
                          </Text>
                        </VStack>
                      ) : null}
                    </Dialog.Body>
                    <Dialog.Footer>
                      <HStack w="full" justify="space-between">
                        <Button variant="ghost" onClick={() => setCreateStep((s) => Math.max(1, s - 1))} disabled={createStep === 1}>
                          Atrás
                        </Button>
                        <HStack>
                          {createStep < 3 ? (
                            <Button
                              colorPalette="purple"
                              onClick={() => {
                                if (createStep === 1 && (!form.nombre.trim() || !form.desde || !form.hasta)) {
                                  toaster.create({ title: 'Nombre y fechas son obligatorios', type: 'warning' });
                                  return;
                                }
                                setCreateStep((s) => Math.min(3, s + 1));
                              }}
                            >
                              Siguiente
                            </Button>
                          ) : (
                            <Button colorPalette="purple" onClick={handleCreate} loading={creating}><FaPlus />Crear
                                                      </Button>
                          )}
                        </HStack>
                      </HStack>
                    </Dialog.Footer>
                  </Dialog.Content>
                </Dialog.Positioner>

              </Portal>
            </Dialog.Root>

            {selectedId ? (
            <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderCol} rounded="2xl" shadow="md" data-tour="objetivos-detalle">
              <Card.Header pb={2}>
                <Flex
                  direction={{ base: 'column', xl: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', xl: 'flex-start' }}
                  gap={4}
                >
                  <Box flex="1" minW={0}>
                    <Heading size="sm">{objetivo ? objetivo.nombre : 'Detalle'}</Heading>
                    {objetivo ? (
                      <Text fontSize="sm" color={sub} mt={1}>
                        {objetivo.desde} → {objetivo.hasta}{' '}
                        {objetivo.actuacionPuntosModo === 'POR_TIPO' ? (
                          <Badge ml={2} colorPalette="cyan" variant="subtle">
                            Actuaciones por tipo
                          </Badge>
                        ) : null}
                        {premiosTxt ? <Badge ml={2} colorPalette="yellow">🎁 {premiosTxt}</Badge> : null}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color={sub} mt={1}>
                        Selecciona un objetivo para ver la tabla.
                      </Text>
                    )}
                  </Box>
                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    gap={2}
                    flexWrap="wrap"
                    align={{ base: 'stretch', sm: 'center' }}
                    w={{ base: 'full', xl: 'auto' }}
                  >
                    <Button size="sm" variant="outline" onClick={openPremios} disabled={!selectedId}>
                      Editar premios
                    </Button>
                    <Button size="sm" variant="outline" onClick={openPuntuacion} disabled={!selectedId}>
                      Configurar puntuación
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      onClick={deleteDisc.onOpen}
                      disabled={!selectedId}><FaTrash />Eliminar
                                          </Button>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={solo}
                        onChange={(e) => setSolo(e.target.value)}
                        maxW={{ base: 'full', sm: '220px' }}
                        flex={{ sm: '1 1 200px' }}
                        minW={0}>
                        {SOLO_OPTS.map((o) => (
                          <option key={o.v} value={o.v}>
                            {o.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        maxW={{ base: 'full', sm: '220px' }}
                        flex={{ sm: '1 1 200px' }}
                        minW={0}>
                        {ORDER_OPTS.map((o) => (
                          <option key={o.v} value={o.v}>
                            Orden: {o.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="purple"
                      onClick={loadDetail}
                      loading={detalleLoading}
                      disabled={!selectedId}
                      alignSelf={{ base: 'stretch', sm: 'center' }}><FaSave />Refrescar
                                          </Button>
                  </Stack>
                </Flex>
              </Card.Header>
              <Card.Body pt={0}>
                {detalleLoading ? (
                  <Flex justify="center" py={14}>
                    <Spinner color="purple.500" />
                  </Flex>
                ) : !objetivo ? (
                  <Text fontSize="sm" color={sub}>
                    Selecciona un objetivo.
                  </Text>
                ) : eventosOrdenados.length === 0 ? (
                  <Text fontSize="sm" color={sub}>
                    No hay eventos en este rango con la configuración actual.
                  </Text>
                ) : (
                  <>
                    {maxColsHint ? (
                      <Text fontSize="xs" color={sub} mb={3}>
                        Hay muchas columnas ({eventosOrdenados.length}). Puedes usar el scroll horizontal y filtrar por tipo.
                      </Text>
                    ) : null}
                    <HStack gap={2} align="center" fontSize="xs" color={sub} mb={2} flexWrap="wrap">
                      <Box
                        w="14px"
                        h="14px"
                        rounded="md"
                        borderWidth="1px"
                        borderColor={justificadoNumBorder}
                        bg={justificadoLegendSwatch}
                        flexShrink={0}
                        aria-hidden
                      />
                      <Text as="span">
                        Recuadro turquesa: falta justificada (0 puntos, sin penalización).
                      </Text>
                    </HStack>
                    <Box overflowX="auto" borderWidth="1px" borderColor={borderCol} rounded="xl" w="full">
                      <Table.Root size="sm" variant="simple" css={{
                        minWidth: 'max-content',
                        tableLayout: 'auto'
                      }}>
                        <Table.Header position="sticky" top={0} bg={headerStickyBg} zIndex={1}>
                          <Table.Row>
                            <Table.ColumnHeader position="sticky" left={0} bg={headerStickyBg} zIndex={2}>
                              Músico
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Total</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Ensayos</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Actuaciones</Table.ColumnHeader>
                            <Table.ColumnHeader>Objetivo</Table.ColumnHeader>
                            {eventosOrdenados.map((ev) => (
                              <Table.ColumnHeader key={ev.key} whiteSpace="nowrap" textAlign="center" minW="112px" maxW="240px">
                                <VStack align="center" gap={0}>
                                  <Text fontSize="xs" fontWeight="800">
                                    {formatFechaActividadEs(ev.fecha)}
                                  </Text>
                                  <Text fontSize="xs" opacity={0.85} lineClamp={2} maxW="220px" textAlign="center">
                                    {ev.tipo === 'ensayo' ? 'Ensayo' : 'Actuación'} · {ev.nombre}
                                  </Text>
                                  {ev.tipo === 'actuacion' && ev.actuacionTipo ? (
                                    <Text fontSize="2xs" opacity={0.72} lineClamp={1} maxW="220px">
                                      {labelTipoActuacion(ev.actuacionTipo, tiposFormacionCatalogo)}
                                    </Text>
                                  ) : null}
                                  <Text fontSize="xs" opacity={0.7}>
                                    {ev.puntos} ptos
                                  </Text>
                                  {ev.penalizacionAusencia ? (
                                    <Text fontSize="xs" opacity={0.7} style={{ color: 'var(--chakra-colors-red-400)' }}>
                                      penaliza {ev.penalizacionAusencia}
                                    </Text>
                                  ) : null}
                                  {ev.tieneOverridePuntos ? (
                                    <Text fontSize="2xs" color="orange.400" fontWeight="600" maxW="220px" textAlign="center">
                                      Puntos fijados manualmente (override)
                                    </Text>
                                  ) : null}
                                </VStack>
                              </Table.ColumnHeader>
                            ))}
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {filas.map((r) => {
                            const label = [r.musico?.nombre, r.musico?.apellidos].filter(Boolean).join(' ');
                            return (
                              <Table.Row key={r.musico.id}>
                                <Table.Cell position="sticky" left={0} bg={headerStickyBg} borderRightWidth="1px" borderColor={borderCol} zIndex={1}>
                                  <Text fontWeight="700" lineClamp={1}>
                                    {label || '—'}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell fontWeight="800" textAlign='end'>
                                  {r.total}
                                </Table.Cell>
                                <Table.Cell textAlign='end'>{r.totalEnsayos}</Table.Cell>
                                <Table.Cell textAlign='end'>{r.totalActuaciones}</Table.Cell>
                                <Table.Cell>
                                  {r.premio ? (
                                    <Badge colorPalette="yellow">
                                      {r.premio.puntosMin}+ · {r.premio.premio}
                                    </Badge>
                                  ) : (
                                    <Text fontSize="xs" color={sub}>
                                      —
                                    </Text>
                                  )}
                                </Table.Cell>
                                {eventosOrdenados.map((ev) => {
                                  const k = ev.key;
                                  const pts = r.puntos?.[k] ?? 0;
                                  const pen = r.penalizaciones?.[k] ?? 0;
                                  const just = r.justificados?.[k] === true;
                                  return (
                                    <Table.Cell key={`${r.musico.id}-${k}`} textAlign="center">
                                      {pts ? (
                                        <Badge colorPalette="purple">{pts}</Badge>
                                      ) : pen ? (
                                        <Badge colorPalette="red" variant="subtle">
                                          {pen}
                                        </Badge>
                                      ) : just ? (
                                        <Tooltip
                                          content="Ausencia justificada: no suma puntos ni penalización."
                                          showArrow
                                          openDelay={300}
                                          positioning={{
                                            placement: "top"
                                          }}
                                        >
                                          <Box
                                            as="span"
                                            display="inline-flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            minW="26px"
                                            h="22px"
                                            px={1.5}
                                            rounded="md"
                                            bg={justificadoNumBg}
                                            borderWidth="1px"
                                            borderColor={justificadoNumBorder}
                                            color={justificadoNumColor}
                                            fontSize="xs"
                                            fontWeight="800"
                                            cursor="default"
                                          >
                                            0
                                          </Box>
                                        </Tooltip>
                                      ) : (
                                        <Text fontSize="xs" color={sub}>
                                          0
                                        </Text>
                                      )}
                                    </Table.Cell>
                                  );
                                })}
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  </>
                )}
              </Card.Body>
            </Card.Root>
            ) : null}

            <Dialog.Root open={premiosDisc.open} size='lg' placement='center' onOpenChange={e => {
              if (!e.open) {
                premiosDisc.onClose();
              }
            }}>
              <Portal>

                <Dialog.Backdrop />
                <Dialog.Positioner>
                  <Dialog.Content>
                    <Dialog.Header>Premios / umbrales</Dialog.Header>
                    <Dialog.CloseTrigger />
                    <Dialog.Body pt={5}>
                      <Text fontSize="sm" color={sub} mb={4}>
                        Se asigna el premio más alto cuyo umbral (puntos mínimos) se haya alcanzado.
                      </Text>

                      {premiosDraft.length === 0 ? (
                        <Text fontSize="sm" color={sub}>
                          No hay premios configurados. Añade uno para empezar.
                        </Text>
                      ) : (
                        <VStack align="stretch" gap={2}>
                          {premiosDraft.map((p, idx) => (
                            <HStack key={idx} gap={2} align="flex-end" flexWrap="wrap">
                              <Field.Root maxW="200px">
                                <Field.Label fontSize="xs" mb={1}>
                                  Puntos mínimos
                                </Field.Label>
                                <Input
                                  size="sm"
                                  type="number"
                                  min={0}
                                  value={p.puntosMin}
                                  onChange={(e) => handlePremioChange(idx, { puntosMin: Number(e.target.value) })}
                                />
                              </Field.Root>
                              <Field.Root flex="1" minW="240px">
                                <Field.Label fontSize="xs" mb={1}>
                                  Premio
                                </Field.Label>
                                <Input
                                  size="sm"
                                  placeholder="Ej: Kindle"
                                  value={p.premio}
                                  onChange={(e) => handlePremioChange(idx, { premio: e.target.value })}
                                />
                              </Field.Root>
                              <Button
                                size="sm"
                                variant="ghost"
                                colorPalette="red"
                                onClick={() => handleRemovePremio(idx)}><FaTrash />Quitar
                                                          </Button>
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </Dialog.Body>
                    <Dialog.Footer>
                      <HStack w="full" justify="space-between" flexWrap="wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleAddPremio}
                          disabled={!selectedId}><FaPlus />Añadir
                                              </Button>
                        <HStack>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleResetPremios}
                            disabled={!premiosDirty}><FaUndo />Revertir
                                                  </Button>
                          <Button
                            size="sm"
                            colorPalette="purple"
                            onClick={handleSavePremios}
                            loading={savingPremios}
                            disabled={!premiosDirty}><FaSave />Guardar
                                                  </Button>
                        </HStack>
                      </HStack>
                    </Dialog.Footer>
                  </Dialog.Content>
                </Dialog.Positioner>

              </Portal>
            </Dialog.Root>

            <Dialog.Root
              open={puntuacionDisc.open}
              size='xl'
              placement='center'
              scrollBehavior="inside"
              onOpenChange={e => {
                if (!e.open) {
                  puntuacionDisc.onClose();
                  setBaseDraft(null);
                  setOverridesDraft({});
                }
              }}
            >
              <Portal>

                <Dialog.Backdrop />
                <Dialog.Positioner>
                  <Dialog.Content maxW="960px" w="full">
                    <Dialog.Header>Configurar puntuación</Dialog.Header>
                    <Dialog.CloseTrigger />
                    <Dialog.Body pt={4}>
                      {baseDraft ? (
                        <VStack align="stretch" gap={5}>
                          <Text fontSize="sm" color={sub}>
                            Define primero los valores por defecto (ensayos y actuaciones). Opcionalmente, más abajo, ajusta una fecha concreta:
                            esos valores sustituyen al cálculo por defecto solo para esa actividad.
                          </Text>
                          <Box borderWidth="1px" borderColor={borderCol} rounded="lg" p={4}>
                            <Text fontWeight="800" fontSize="sm" mb={3}>
                              Ensayos (por defecto)
                            </Text>
                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                              <Field.Root>
                                <Field.Label fontSize="xs">Puntos si asiste (presente)</Field.Label>
                                <Input
                                  size="sm"
                                  type="number"
                                  min={0}
                                  value={baseDraft.puntosEnsayoDefault}
                                  onChange={(e) =>
                                    onBaseDraftChange((d) => (d ? { ...d, puntosEnsayoDefault: Number(e.target.value) } : d))
                                  }
                                />
                              </Field.Root>
                              <Field.Root>
                                <Field.Label fontSize="xs">Penalización si falta (ausente)</Field.Label>
                                <Input
                                  size="sm"
                                  type="number"
                                  min={0}
                                  value={baseDraft.penalizacionEnsayoAusenciaDefault}
                                  onChange={(e) =>
                                    onBaseDraftChange((d) =>
                                      d ? { ...d, penalizacionEnsayoAusenciaDefault: Number(e.target.value) } : d,
                                    )
                                  }
                                />
                              </Field.Root>
                            </SimpleGrid>
                          </Box>
                          <Box borderWidth="1px" borderColor={borderCol} rounded="lg" p={4}>
                            <Text fontWeight="800" fontSize="sm" mb={2}>
                              Actuaciones (por defecto)
                            </Text>
                            <Field.Root mb={3}>
                              <Field.Label fontSize="xs">Modo</Field.Label>
                              <RadioGroup.Root
                                value={baseDraft.actuacionPuntosModo}
                                onValueChange={(v) => onBaseDraftChange((d) => (d ? { ...d, actuacionPuntosModo: v } : d))}>
                                <Stack direction="column" gap={2}>
                                  <RadioGroup.Item value="GLOBAL"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Mismo valor para todas las actuaciones</RadioGroup.ItemText></RadioGroup.Item>
                                  <RadioGroup.Item value="POR_TIPO"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Distinto según el tipo (procesión, concierto, certamen…)</RadioGroup.ItemText></RadioGroup.Item>
                                </Stack>
                              </RadioGroup.Root>
                            </Field.Root>
                            {baseDraft.actuacionPuntosModo === 'GLOBAL' ? (
                              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                <Field.Root>
                                  <Field.Label fontSize="xs">Puntos si asiste</Field.Label>
                                  <Input
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={baseDraft.puntosActuacionDefault}
                                    onChange={(e) =>
                                      onBaseDraftChange((d) => (d ? { ...d, puntosActuacionDefault: Number(e.target.value) } : d))
                                    }
                                  />
                                </Field.Root>
                                <Field.Root>
                                  <Field.Label fontSize="xs">Penalización si no asiste</Field.Label>
                                  <Input
                                    size="sm"
                                    type="number"
                                    min={0}
                                    value={baseDraft.penalizacionActuacionAusenciaDefault}
                                    onChange={(e) =>
                                      onBaseDraftChange((d) =>
                                        d ? { ...d, penalizacionActuacionAusenciaDefault: Number(e.target.value) } : d,
                                      )
                                    }
                                  />
                                </Field.Root>
                              </SimpleGrid>
                            ) : (
                              <>
                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
                                  <Field.Root>
                                    <Field.Label fontSize="xs">Penalización global (por defecto)</Field.Label>
                                    <Input
                                      size="sm"
                                      type="number"
                                      min={0}
                                      value={baseDraft.penalizacionActuacionAusenciaDefault}
                                      onChange={(e) =>
                                        onBaseDraftChange((d) =>
                                          d ? { ...d, penalizacionActuacionAusenciaDefault: Number(e.target.value) } : d,
                                        )
                                      }
                                    />
                                    <Field.HelperText>Si dejas la penalización de un tipo vacía, se usa esta.</Field.HelperText>
                                  </Field.Root>
                                  <Field.Root>
                                    <Field.Label fontSize="xs">Puntos globales (reserva)</Field.Label>
                                    <Input
                                      size="sm"
                                      type="number"
                                      min={0}
                                      value={baseDraft.puntosActuacionDefault}
                                      onChange={(e) =>
                                        onBaseDraftChange((d) => (d ? { ...d, puntosActuacionDefault: Number(e.target.value) } : d))
                                      }
                                    />
                                    <Field.HelperText>Se aplica a tipos que no personalices abajo.</Field.HelperText>
                                  </Field.Root>
                                </SimpleGrid>
                                <Text fontSize="xs" color={sub} mb={2}>
                                  Por cada tipo de actuación. Si más abajo fijas puntos para una fecha concreta, ese ajuste prevalece sobre la
                                  fila de tipo correspondiente.
                                </Text>
                                <Box maxH="280px" overflowY="auto" borderWidth="1px" borderColor={borderCol} rounded="md">
                                  <Table.Root size="sm">
                                    <Table.Header position="sticky" top={0} bg={headerStickyBg} zIndex={1}>
                                      <Table.Row>
                                        <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                                        <Table.ColumnHeader textAlign='end'>Puntos (asiste)</Table.ColumnHeader>
                                        <Table.ColumnHeader textAlign='end'>Penal. (falta)</Table.ColumnHeader>
                                      </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                      {tiposFormacionCatalogo.map((tf) => {
                                        const codigo = String(tf?.codigo || '').trim();
                                        if (!codigo) return null;
                                        const etiqueta = tf.nombre || codigo;
                                        const row = baseDraft.porTipo[codigo] || { puntos: '0', penalizacionAusencia: '' };
                                        return (
                                          <Table.Row key={codigo}>
                                            <Table.Cell fontSize="xs" whiteSpace="nowrap">
                                              {etiqueta}
                                            </Table.Cell>
                                            <Table.Cell>
                                              <Input
                                                size="xs"
                                                type="number"
                                                min={0}
                                                textAlign="right"
                                                value={row.puntos}
                                                onChange={(e) =>
                                                  onBaseDraftChange((d) => {
                                                    if (!d) return d;
                                                    const prev = d.porTipo[codigo] || { puntos: '0', penalizacionAusencia: '' };
                                                    return {
                                                      ...d,
                                                      porTipo: {
                                                        ...d.porTipo,
                                                        [codigo]: { ...prev, puntos: e.target.value },
                                                      },
                                                    };
                                                  })
                                                }
                                              />
                                            </Table.Cell>
                                            <Table.Cell>
                                              <Input
                                                size="xs"
                                                type="number"
                                                min={0}
                                                textAlign="right"
                                                placeholder={String(baseDraft.penalizacionActuacionAusenciaDefault ?? 0)}
                                                value={row.penalizacionAusencia}
                                                onChange={(e) =>
                                                  onBaseDraftChange((d) => {
                                                    if (!d) return d;
                                                    const prev = d.porTipo[codigo] || { puntos: '0', penalizacionAusencia: '' };
                                                    return {
                                                      ...d,
                                                      porTipo: {
                                                        ...d.porTipo,
                                                        [codigo]: { ...prev, penalizacionAusencia: e.target.value },
                                                      },
                                                    };
                                                  })
                                                }
                                              />
                                            </Table.Cell>
                                          </Table.Row>
                                        );
                                      })}
                                    </Table.Body>
                                  </Table.Root>
                                </Box>
                              </>
                            )}
                          </Box>

                          <Separator />
                          <Box>
                            <Flex justify="space-between" align={{ base: 'stretch', sm: 'center' }} gap={3} mb={1} flexWrap="wrap">
                              <Text fontWeight="800" fontSize="sm">
                                Por actividad concreta (opcional)
                              </Text>
                              <NativeSelect.Root>
                                <NativeSelect.Field
                                  size="sm"
                                  maxW={{ base: 'full', sm: '260px' }}
                                  value={overrideFechaOrder}
                                  onChange={(e) => setOverrideFechaOrder(e.target.value)}
                                  aria-label="Ordenar actividades por fecha">
                                  <option value="fecha_desc">Fecha: recientes → antiguas</option>
                                  <option value="fecha">Fecha: antiguas → recientes</option>
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </Flex>
                            <Text fontSize="sm" color={sub} mb={3}>
                              Solo aparecen las actividades visibles con los filtros actuales (todo / solo ensayos / solo actuaciones).
                              Deja los valores como en &quot;Por defecto&quot; y pulsa &quot;Quitar override&quot; para volver al cálculo automático.
                            </Text>
                            {eventosOverridesOrdenados.length === 0 ? (
                              <Text fontSize="sm" color={sub}>
                                No hay actividades en este rango con la configuración actual.
                              </Text>
                            ) : (
                              <VStack align="stretch" gap={2} maxH="360px" overflowY="auto">
                                {eventosOverridesOrdenados.map((ev) => {
                                  const evKey = ev?.key || `${ev.tipo}:${ev.id}`;
                                  const hasOv = Object.prototype.hasOwnProperty.call(overridesDraft || {}, evKey);
                                  const currentPts = hasOv ? overridesDraft?.[evKey]?.puntos : ev.puntos;
                                  const currentPen = hasOv ? (overridesDraft?.[evKey]?.penalizacionAusencia ?? '') : ev.penalizacionAusencia;
                                  const labelTipo = ev.tipo === 'ensayo' ? 'Ensayo' : 'Actuación';
                                  return (
                                    <Box key={evKey} borderWidth="1px" borderColor={borderCol} rounded="lg" p={3}>
                                      <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                                        <Box minW={0} flex="1">
                                          <Text fontWeight="800" fontSize="sm" lineClamp={2}>
                                            {formatFechaActividadEs(ev.fecha)} · {labelTipo} · {ev.nombre}
                                          </Text>
                                          <Text fontSize="xs" color={sub} mt={1}>
                                            Calculado por defecto: {ev.puntos} ptos · penaliza {ev.penalizacionAusencia}
                                            {hasOv ? (
                                              <Badge ml={2} colorPalette="purple">
                                                sustituye defecto
                                              </Badge>
                                            ) : null}
                                          </Text>
                                        </Box>
                                        <HStack gap={2} align="flex-end" flexWrap="wrap">
                                          <Field.Root maxW="160px">
                                            <Field.Label fontSize="xs" mb={1}>
                                              Puntos (si asiste)
                                            </Field.Label>
                                            <Input
                                              size="sm"
                                              type="number"
                                              min={0}
                                              value={Number(currentPts) || 0}
                                              onChange={(e) => handleOverrideChange(evKey, { puntos: Number(e.target.value) })}
                                            />
                                          </Field.Root>
                                          <Field.Root maxW="180px">
                                            <Field.Label fontSize="xs" mb={1}>
                                              Penaliza (si falta)
                                            </Field.Label>
                                            <Input
                                              size="sm"
                                              type="number"
                                              min={0}
                                              value={currentPen === undefined || currentPen === null ? '' : currentPen}
                                              onChange={(e) =>
                                                handleOverrideChange(evKey, {
                                                  penalizacionAusencia: e.target.value === '' ? undefined : Number(e.target.value),
                                                })
                                              }
                                            />
                                          </Field.Root>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleOverrideClear(evKey)}
                                            disabled={!hasOv}
                                          >
                                            Quitar override
                                          </Button>
                                        </HStack>
                                      </Flex>
                                    </Box>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color={sub}>
                          Cierra y abre de nuevo desde el detalle del objetivo.
                        </Text>
                      )}
                    </Dialog.Body>
                    <Dialog.Footer>
                      <HStack w="full" justify="space-between" flexWrap="wrap">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            puntuacionDisc.onClose();
                            setBaseDraft(null);
                            setOverridesDraft({});
                          }}
                        >
                          Cerrar
                        </Button>
                        <HStack>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleResetPuntuacion}
                            disabled={!puntuacionDirty}><FaUndo />Revertir
                                                  </Button>
                          <Button
                            size="sm"
                            colorPalette="purple"
                            onClick={handleSavePuntuacion}
                            loading={savingPuntuacion}
                            disabled={!baseDraft || !puntuacionDirty}><FaSave />Guardar
                                                  </Button>
                        </HStack>
                      </HStack>
                    </Dialog.Footer>
                  </Dialog.Content>
                </Dialog.Positioner>

              </Portal>
            </Dialog.Root>

            <Dialog.Root
              open={deleteDisc.open}
              initialFocusEl={() => cancelDeleteRef.current}
              role='alertdialog'
              onOpenChange={e => {
                if (!e.open) {
                  deleteDisc.onClose();
                }
              }}>
              <Portal>

                <Dialog.Backdrop />
                <Dialog.Positioner>
                  <Dialog.Content>
                    <Dialog.Header fontSize="lg" fontWeight="bold">
                      Eliminar objetivo
                    </Dialog.Header>
                    <Dialog.Body>
                      Se eliminará «{selectedObj?.nombre || 'este objetivo'}» y su configuración (premios y ajustes por fecha). Esta acción no se puede deshacer.
                    </Dialog.Body>
                    <Dialog.Footer>
                      <Button ref={cancelDeleteRef} onClick={deleteDisc.onClose}>
                        Cancelar
                      </Button>
                      <Button colorPalette="red" ml={3} onClick={handleDeleteObjetivo} loading={deletingObjetivo}>
                        Eliminar
                      </Button>
                    </Dialog.Footer>
                  </Dialog.Content>
                </Dialog.Positioner>

              </Portal>
            </Dialog.Root>
          </VStack>
        </Box>
      </Box>
    </AppLayout>
  );
}

